import { ApolloProvider } from "@apollo/client/react";
import { CssBaseline, GlobalStyles } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import { AppRoutes } from "./AppRoutes.tsx";
import { clientKernelContext, createClientKernel } from "./Kernel/ClientKernel.ts";
import "react-toastify/dist/ReactToastify.css";
import "./styles/toastify.css";
import { type FC, useRef } from "react";
import { BrowserRouter } from "react-router-dom";
import { movesofconsequenceMuiTheme } from "./Components/Theme/movesofconsequenceMuiTheme.ts";
import { UiI18nProvider } from "./I18n";
import { StartupAnimation } from "./Components/Startup/StartupAnimation.tsx";

export interface ClientContainerWrapperProps extends React.PropsWithChildren {
	kernelResult?: ReturnType<typeof createClientKernel>;
}

export const ClientContainerWrapper: FC<ClientContainerWrapperProps> = ({
	kernelResult,
	children,
}) => {
	const ref = useRef<ReturnType<typeof createClientKernel>>(
		kernelResult ?? createClientKernel("storybook"),
	);
	kernelResult = ref.current;
	const { client, store, env } = kernelResult;

	return (
		<clientKernelContext.Provider value={kernelResult}>
			<Provider store={store}>
				<UiI18nProvider>
					<ApolloProvider client={client}>
						<ThemeProvider theme={movesofconsequenceMuiTheme}>
							<CssBaseline />
							<GlobalStyles
								styles={{
									"#app": {
										minHeight: "100vh",
									},
									":focus-visible": {
										outline: "2px solid #58c7ff",
										outlineOffset: 2,
									},
								}}
							/>
							<BrowserRouter>
								{env === "storybook" ? (
									children
								) : (
									<StartupAnimation kernel={kernelResult}>
										<AppRoutes />
									</StartupAnimation>
								)}
								<ToastContainer
									position="bottom-right"
									theme="dark"
									autoClose={4500}
									hideProgressBar={false}
									closeOnClick
									pauseOnFocusLoss
									pauseOnHover
									limit={4}
								/>
							</BrowserRouter>
						</ThemeProvider>
					</ApolloProvider>
				</UiI18nProvider>
			</Provider>
		</clientKernelContext.Provider>
	);
};
